import React, { Component } from 'react';

import { CheckoutPortalFormattedMessage } from 'components/Core/CheckoutPrtalForamattedMessage/CheckoutPortalFormattedMessage';
import FormattedMessageHTML from 'components/Core/FormattedMessageHTML';
import Card from 'components/Shared/Card/Card';
import ErrorAlertIcon from 'components/Shared/Icons/ErrorAlertIcon';
import WarningAlertIcon from 'components/Shared/Icons/WarningAlertIcon';
import { SplunkReporter } from 'reporting/splunk/SplunkReporter';
import { colors, fontSize } from 'styles/cp';
import { ActivityInfo } from 'types/SplunkLogs';

const splunkReporter = SplunkReporter.getInstance();

const logger = 'components/Shared/MessageCard/MessageCard';
type Status = 'error' | 'info' | 'warn' | 'orangeWarn';

export type Props = {
  status?: Status;
  header?: string;
  text: string;
  messageValues?: {
    [key: string]: string | React.JSX.Element | ((chunks: string) => React.JSX.Element);
  };
  errorMessageIconPosition?: 'start' | 'end' | 'center';
  activityInfo?: ActivityInfo;
  messageFontSize?: string;
};
interface State {}

class MessageCard extends Component<Props, State> {
  errorRef: React.RefObject<any>;

  constructor(props: Readonly<Props>) {
    super(props);
    this.errorRef = React.createRef();
  }

  componentDidMount() {
    if (this.errorRef.current) {
      window.scrollTo({ behavior: 'smooth', top: this.errorRef.current.offsetTop });
    }
    const { status, text, activityInfo } = this.props;
    splunkReporter.contextual({
      logInfo: { logLevel: 'info', logger },
      event: 'view',
      action: 'render',
      activityInfo: {
        status: status === 'error' ? status : 'success',
        activityObject: 'messageCard',
        message: text,
        ...activityInfo,
      },
    });
  }

  render() {
    const { status = 'error', header, text, messageValues = {}, messageFontSize } = this.props;

    const statusColors: Record<Status, string> = {
      error: colors.darkError,
      info: colors.intuit_blue,
      orangeWarn: colors.orangeWarn,
      warn: colors.warn,
    };
    const color = statusColors[status] ?? statusColors['warn'];
    const exclamationColor =
      status === 'info' || status === 'orangeWarn' ? colors.white : colors.gray;

    return (
      <div ref={this.errorRef} className="error-message-root">
        <Card borderRadius="4px">
          <div className="error-message-wrapper">
            <div className="error-message-icon">
              {status === 'error' ? (
                <ErrorAlertIcon color={color} height={23} width={23} />
              ) : (
                <WarningAlertIcon
                  color={color}
                  height={24}
                  width={24}
                  exclamationColor={exclamationColor}
                />
              )}
            </div>
            <div className="error-message-content">
              {header && (
                <span className="error-message-header">
                  <CheckoutPortalFormattedMessage
                    id={header}
                    defaultMessage="Error"
                    values={messageValues}
                  />
                </span>
              )}
              <span className="error-message-text">
                {/* Check if messageValues contains any functions (clickable links) */}
                {messageValues &&
                Object.values(messageValues).some((value) => typeof value === 'function') ? (
                  <CheckoutPortalFormattedMessage
                    id={text}
                    defaultMessage="Payment wasn't successful. Try again or contact your seller."
                    values={messageValues}
                  />
                ) : (
                  <FormattedMessageHTML
                    id={text}
                    defaultMessage="Payment wasn't successful. Try again or contact your seller."
                    values={messageValues as Record<string, string>}
                  />
                )}
              </span>
            </div>
          </div>
        </Card>
        {/*language=SCSS*/}
        <style jsx>{`
          .error-message-root {
            margin-bottom: 20px;
          }
          .error-message-wrapper {
            display: flex;
            flex-direction: row;
            border: solid 1px ${color};
            border-radius: 4px;
            padding: 20px;
          }

          .error-message-icon {
            display: flex;
            flex-direction: column;
            justify-content: ${this.props.errorMessageIconPosition || 'center'};
          }

          .error-message-content {
            margin-left: 20px;
            display: flex;
            flex-direction: column;
            justify-content: space-around;
          }
          .error-message-header {
            font-size: ${fontSize.sm};
            color: ${color};
            //margin-bottom: 6px;
          }

          .error-message-text {
            font-size: ${messageFontSize ?? fontSize.xxs};
            color: ${colors.gray};
          }

          .error-message-text {
            :global(a) {
              color: ${colors.blue};
              text-decoration: none;
            }

            :global(a.action-link) {
              cursor: pointer;
              text-decoration: underline;
            }
          }
        `}</style>
      </div>
    );
  }
}

export default MessageCard;
